import { useState, useMemo } from 'react'
import { pdf } from '@react-pdf/renderer'
import { toast } from 'react-hot-toast'
import { useAuth } from '@/features/auth/AuthProvider'
import type { Retiro, Venta } from '@/types/database'
import { PlusIcon, CloseIcon, CheckIcon, SearchIcon } from '@/components/shared/Icons'
import { useConfiguracion } from '@/features/configuracion/hooks'
import { RetiroDocument } from './RetiroPDF'
import {
  useRetiros, useVentasParaRetiro, useCrearRetiro, useRegistrarEntrega,
  type RetiroFiltros,
} from './hooks'

const fmt = (n: number) =>
  new Intl.NumberFormat('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n)

const fmtFecha = (iso: string) =>
  new Date(iso).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' })

const ESTADO_CONFIG = {
  pendiente: { label: 'Pendiente', pill: 'me-pill-warn' },
  parcial:   { label: 'Parcial',   pill: 'me-pill-acid' },
  completo:  { label: 'Completo',  pill: 'me-pill-ok'   },
} as const

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function RetirosPage() {
  const { user } = useAuth()
  const [drawerOpen, setDrawerOpen]     = useState(false)
  const [detalle, setDetalle]           = useState<Retiro | null>(null)
  const [filtroEstado, setFiltroEstado] = useState<string>('all')

  const filtros: RetiroFiltros = filtroEstado === 'all' ? {} : { estado: filtroEstado as RetiroFiltros['estado'] }
  const { data: retiros = [], isLoading } = useRetiros(filtros)

  const pendientes = retiros.filter(r => r.estado === 'pendiente').length
  const parciales  = retiros.filter(r => r.estado === 'parcial').length

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <div className="me-eyebrow" style={{ marginBottom: 4 }}>Inventario</div>
          <h1 className="me-page-title">Retiros</h1>
        </div>
        <button
          className="me-btn me-btn-primary"
          onClick={() => setDrawerOpen(true)}
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}
        >
          <PlusIcon size={14} strokeWidth={2} /> Nuevo retiro
        </button>
      </div>

      {/* Filtro tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {(['all', 'pendiente', 'parcial', 'completo'] as const).map(e => {
          const active = filtroEstado === e
          const badge  = e === 'pendiente' ? pendientes : e === 'parcial' ? parciales : 0
          return (
            <button
              key={e}
              onClick={() => setFiltroEstado(e)}
              style={{
                padding: '6px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
                background: active ? 'var(--me-text)' : 'var(--me-surface)',
                color: active ? 'var(--me-bg)' : 'var(--me-text-2)',
                fontFamily: 'var(--me-sans)', fontSize: 13, fontWeight: active ? 600 : 400,
                transition: 'background var(--me-dur-short), color var(--me-dur-short)',
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              {e === 'all' ? 'Todos' : ESTADO_CONFIG[e].label}
              {badge > 0 && (
                <span style={{
                  background: e === 'parcial' ? 'var(--me-acid)' : 'var(--me-warn)',
                  color: e === 'parcial' ? 'var(--me-acid-ink)' : '#fff',
                  borderRadius: '50%', width: 18, height: 18,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 10, fontWeight: 700,
                }}>
                  {badge}
                </span>
              )}
            </button>
          )
        })}
      </div>

      {/* Tabla */}
      <div className="me-card" style={{ padding: 0, overflow: 'clip' }}>
        {isLoading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--me-text-3)' }}>Cargando…</div>
        ) : retiros.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: 'var(--me-text-3)', fontSize: 13.5 }}>
            {filtroEstado === 'all'
              ? 'Sin retiros registrados — creá el primero desde una venta existente'
              : `Sin retiros con estado "${ESTADO_CONFIG[filtroEstado as keyof typeof ESTADO_CONFIG]?.label ?? filtroEstado}"`}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="me-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Fecha</th>
                  <th>Venta</th>
                  <th>Cliente</th>
                  <th>Estado</th>
                  <th style={{ textAlign: 'right' }}>Entregado</th>
                  <th style={{ textAlign: 'right' }}>Pendiente</th>
                </tr>
              </thead>
              <tbody>
                {retiros.map((r, i) => {
                  const venta = (r as unknown as { ventas?: { numero: number; tipo: string; clientes?: { nombre: string } } }).ventas
                  const items = r.retiro_items ?? []
                  const totalSol = items.reduce((s, it) => s + it.cantidad_solicitada, 0)
                  const totalEnt = items.reduce((s, it) => s + it.cantidad_entregada, 0)
                  const pendiente = totalSol - totalEnt

                  return (
                    <tr
                      key={r.id}
                      style={{ background: i % 2 === 0 ? 'transparent' : 'var(--me-surface-2)', cursor: 'pointer' }}
                      onClick={() => setDetalle(r)}
                    >
                      <td style={{ fontFamily: 'var(--me-mono)', fontSize: 12, color: 'var(--me-text-3)' }}>
                        #{String(r.numero).padStart(5, '0')}
                      </td>
                      <td style={{ fontSize: 13, color: 'var(--me-text-2)' }}>{fmtFecha(r.created_at)}</td>
                      <td style={{ fontFamily: 'var(--me-mono)', fontSize: 13 }}>
                        {venta ? `#${String(venta.numero).padStart(5, '0')}` : '—'}
                        {venta?.tipo === 'mayorista' && (
                          <span className="me-pill me-pill-mayor" style={{ marginLeft: 6 }}>Mayor</span>
                        )}
                      </td>
                      <td style={{ fontWeight: 500, fontSize: 13 }}>
                        {venta?.clientes?.nombre ?? <span style={{ color: 'var(--me-text-4)' }}>Sin cliente</span>}
                      </td>
                      <td>
                        <span className={`me-pill ${ESTADO_CONFIG[r.estado].pill}`}>
                          {ESTADO_CONFIG[r.estado].label}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right', fontFamily: 'var(--me-mono)', fontSize: 13, color: 'var(--me-ok)' }}>
                        {totalEnt > 0 ? totalEnt : <span style={{ color: 'var(--me-text-4)' }}>—</span>}
                      </td>
                      <td style={{ textAlign: 'right', fontFamily: 'var(--me-mono)', fontSize: 13 }}>
                        {pendiente > 0
                          ? <span style={{ color: 'var(--me-warn)', fontWeight: 600 }}>{pendiente}</span>
                          : <span style={{ color: 'var(--me-ok)' }}>✓</span>}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {drawerOpen && (
        <NuevoRetiroDrawer
          usuarioId={user?.id ?? ''}
          onClose={() => setDrawerOpen(false)}
        />
      )}

      {detalle && (
        <RetiroDetalleModal
          retiro={detalle}
          onClose={() => setDetalle(null)}
        />
      )}
    </div>
  )
}

// ─── Nuevo retiro drawer ──────────────────────────────────────────────────────

interface ItemInput {
  ventaItemId: string
  nombre: string
  marca: string | null
  cantidad: number    // max de la venta
  cantidadSolicitada: number
}

function NuevoRetiroDrawer({ usuarioId, onClose }: { usuarioId: string; onClose: () => void }) {
  const { data: ventas = [] } = useVentasParaRetiro()
  const crear = useCrearRetiro()

  const [busqueda, setBusqueda]   = useState('')
  const [ventaId, setVentaId]     = useState<string>('')
  const [items, setItems]         = useState<ItemInput[]>([])
  const [notas, setNotas]         = useState('')

  const ventaSeleccionada = ventas.find(v => v.id === ventaId) ?? null

  const ventasFiltradas = useMemo(() => {
    const q = busqueda.toLowerCase()
    if (!q) return ventas
    return ventas.filter(v => {
      const cliente = (v as unknown as { clientes?: { nombre: string } }).clientes?.nombre ?? ''
      return (
        String(v.numero).includes(q) ||
        cliente.toLowerCase().includes(q)
      )
    })
  }, [ventas, busqueda])

  function seleccionarVenta(v: Venta) {
    setVentaId(v.id)
    setBusqueda('')
    const ventaItems = (v as unknown as { venta_items?: Array<{ id: string; cantidad: number; productos?: { nombre: string; marca?: string } }> }).venta_items ?? []
    setItems(ventaItems.map(vi => ({
      ventaItemId: vi.id,
      nombre: vi.productos?.nombre ?? '—',
      marca: vi.productos?.marca ?? null,
      cantidad: vi.cantidad,
      cantidadSolicitada: vi.cantidad,
    })))
  }

  function setCantidad(ventaItemId: string, val: number) {
    setItems(prev => prev.map(i =>
      i.ventaItemId === ventaItemId
        ? { ...i, cantidadSolicitada: Math.min(Math.max(1, val), i.cantidad) }
        : i
    ))
  }

  async function handleSubmit() {
    if (!ventaId) { toast.error('Seleccioná una venta'); return }
    if (items.length === 0) { toast.error('La venta no tiene artículos'); return }
    try {
      const { numero } = await crear.mutateAsync({
        ventaId,
        items: items.map(i => ({ ventaItemId: i.ventaItemId, cantidadSolicitada: i.cantidadSolicitada })),
        notas,
        usuarioId,
      })
      toast.success(`Retiro #${String(numero).padStart(5, '0')} creado`)
      onClose()
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Error al crear retiro')
    }
  }

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 38, background: 'rgba(0,0,0,0.28)' }} />
      <div className="me-drawer-in" style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 45,
        width: 520, background: 'var(--me-surface)',
        boxShadow: '-8px 0 40px rgba(0,0,0,0.14)',
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{
          padding: '18px 24px', borderBottom: '1px solid var(--me-line)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
        }}>
          <div>
            <div className="me-eyebrow" style={{ marginBottom: 2 }}>Inventario</div>
            <h2 className="me-section-title" style={{ margin: 0 }}>Nuevo retiro</h2>
          </div>
          <button className="me-icon-btn" onClick={onClose}><CloseIcon size={18} /></button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 24 }}>

          {/* Buscar venta */}
          <section>
            <DrawerLabel>Venta de origen</DrawerLabel>
            {ventaSeleccionada ? (
              <VentaCard
                venta={ventaSeleccionada}
                onDeselect={() => { setVentaId(''); setItems([]) }}
              />
            ) : (
              <>
                <div style={{ position: 'relative' }}>
                  <span style={{
                    position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)',
                    color: 'var(--me-text-3)', pointerEvents: 'none', display: 'flex',
                  }}>
                    <SearchIcon size={14} strokeWidth={1.8} />
                  </span>
                  <input
                    className="me-input"
                    style={{ width: '100%', paddingLeft: 34 }}
                    placeholder="Buscar por número de venta o nombre de cliente…"
                    value={busqueda}
                    onChange={e => setBusqueda(e.target.value)}
                    autoFocus
                  />
                </div>
                {ventasFiltradas.length > 0 && (
                  <div style={{
                    marginTop: 8, borderRadius: 10, border: '1px solid var(--me-line)',
                    background: 'var(--me-surface)', overflow: 'hidden',
                    maxHeight: 260, overflowY: 'auto',
                  }}>
                    {ventasFiltradas.slice(0, 12).map((v, idx) => {
                      const cliente = (v as unknown as { clientes?: { nombre: string } }).clientes?.nombre
                      return (
                        <button
                          key={v.id}
                          type="button"
                          onClick={() => seleccionarVenta(v)}
                          style={{
                            width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer',
                            borderBottom: idx < ventasFiltradas.length - 1 ? '1px solid var(--me-line-2)' : 'none',
                            textAlign: 'left', transition: 'background var(--me-dur-micro)',
                          }}
                          onMouseEnter={e => (e.currentTarget.style.background = 'var(--me-surface-2)')}
                          onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                        >
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ fontFamily: 'var(--me-mono)' }}>
                                #{String(v.numero).padStart(5, '0')}
                              </span>
                              {v.tipo === 'mayorista' && (
                                <span className="me-pill me-pill-mayor">Mayor</span>
                              )}
                            </div>
                            <div style={{ fontSize: 11, color: 'var(--me-text-3)', marginTop: 2 }}>
                              {cliente ?? 'Sin cliente'} · {fmtFecha(v.created_at)}
                            </div>
                          </div>
                          <span style={{ fontFamily: 'var(--me-mono)', fontSize: 13, fontWeight: 500 }}>
                            ${fmt(v.total)}
                          </span>
                        </button>
                      )
                    })}
                  </div>
                )}
                {!busqueda && (
                  <p style={{ marginTop: 8, fontSize: 12, color: 'var(--me-text-4)' }}>
                    Escribí para filtrar entre {ventas.length} ventas disponibles
                  </p>
                )}
              </>
            )}
          </section>

          {/* Artículos */}
          {items.length > 0 && (
            <section>
              <DrawerLabel>Artículos del retiro</DrawerLabel>
              <div style={{
                display: 'grid', gridTemplateColumns: '1fr 80px 80px',
                gap: 6, padding: '0 4px 6px', borderBottom: '1px solid var(--me-line)',
              }}>
                {['Producto', 'Vendido', 'A retirar'].map((h, i) => (
                  <div key={h} style={{
                    fontSize: 10, fontWeight: 700, letterSpacing: '0.07em',
                    textTransform: 'uppercase', color: 'var(--me-text-3)',
                    textAlign: i > 0 ? 'center' : 'left',
                  }}>{h}</div>
                ))}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {items.map((item, idx) => (
                  <div key={item.ventaItemId} style={{
                    display: 'grid', gridTemplateColumns: '1fr 80px 80px',
                    gap: 6, alignItems: 'center', padding: '8px 4px',
                    borderBottom: idx < items.length - 1 ? '1px solid var(--me-line-2)' : 'none',
                  }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.nombre}
                      </div>
                      {item.marca && (
                        <div style={{ fontSize: 11, color: 'var(--me-text-3)' }}>{item.marca}</div>
                      )}
                    </div>
                    <div style={{ textAlign: 'center', fontFamily: 'var(--me-mono)', fontSize: 13, color: 'var(--me-text-3)' }}>
                      {item.cantidad}
                    </div>
                    <input
                      className="me-input"
                      style={{ fontFamily: 'var(--me-mono)', textAlign: 'center', padding: '5px 4px', fontSize: 13, width: '100%' }}
                      type="number"
                      min={1}
                      max={item.cantidad}
                      value={item.cantidadSolicitada}
                      onChange={e => setCantidad(item.ventaItemId, parseInt(e.target.value) || 1)}
                    />
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Notas */}
          <section>
            <DrawerLabel optional>Notas</DrawerLabel>
            <textarea
              className="me-input"
              style={{ width: '100%', minHeight: 64, resize: 'vertical', lineHeight: 1.5, fontSize: 13 }}
              value={notas}
              onChange={e => setNotas(e.target.value)}
              placeholder="Observaciones, turno de entrega…"
            />
          </section>
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 24px 18px', borderTop: '1px solid var(--me-line)', flexShrink: 0 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="me-btn me-btn-ghost" onClick={onClose} style={{ flex: 1 }}>
              Cancelar
            </button>
            <button
              className="me-btn me-btn-primary"
              onClick={handleSubmit}
              disabled={crear.isPending || !ventaId}
              style={{ flex: 2 }}
            >
              {crear.isPending ? 'Creando…' : 'Crear retiro'}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

// ─── Detalle modal ─────────────────────────────────────────────────────────────

function RetiroDetalleModal({ retiro, onClose }: { retiro: Retiro; onClose: () => void }) {
  const registrar = useRegistrarEntrega()
  const { data: config } = useConfiguracion()
  const [generandoPDF, setGenerandoPDF] = useState(false)

  async function handleDescargarPDF() {
    if (!config) { toast.error('Cargando configuración, intentá de nuevo'); return }
    setGenerandoPDF(true)
    try {
      const blob = await pdf(<RetiroDocument retiro={retiro} config={config} />).toBlob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `retiro-${String(retiro.numero).padStart(5, '0')}.pdf`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch {
      toast.error('Error al generar el PDF')
    } finally {
      setGenerandoPDF(false)
    }
  }

  type EntregaMap = Record<string, number>
  const [entregas, setEntregas] = useState<EntregaMap>(() => {
    const initial: EntregaMap = {}
    for (const item of retiro.retiro_items ?? []) {
      initial[item.id] = item.cantidad_entregada
    }
    return initial
  })

  const venta = (retiro as unknown as { ventas?: { numero: number; tipo: string; clientes?: { nombre: string } } }).ventas
  const items = retiro.retiro_items ?? []
  const hayPendiente = items.some(i => entregas[i.id] < i.cantidad_solicitada)

  async function handleGuardar() {
    try {
      await registrar.mutateAsync({
        retiroId: retiro.id,
        entregas: items.map(i => ({ itemId: i.id, cantidadEntregada: entregas[i.id] ?? i.cantidad_entregada })),
      })
      toast.success('Entrega registrada')
      onClose()
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Error al registrar entrega')
    }
  }

  function entregarTodo() {
    const todo: EntregaMap = {}
    for (const item of items) {
      todo[item.id] = item.cantidad_solicitada
    }
    setEntregas(todo)
  }

  return (
    <>
      <div className="me-overlay me-overlay-in" onClick={onClose} />
      <div className="me-modal me-modal-in" style={{ maxWidth: 580 }}>
        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
          padding: '20px 24px', borderBottom: '1px solid var(--me-line)',
        }}>
          <div>
            <h2 className="me-section-title" style={{ margin: 0 }}>
              Retiro #{String(retiro.numero).padStart(5, '0')}
            </h2>
            <div style={{ fontSize: 12, color: 'var(--me-text-3)', marginTop: 4 }}>
              {venta ? `Venta #${String(venta.numero).padStart(5, '0')}` : '—'}
              {venta?.clientes?.nombre && ` · ${venta.clientes.nombre}`}
              {' · '}
              {fmtFecha(retiro.created_at)}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span className={`me-pill ${ESTADO_CONFIG[retiro.estado].pill}`}>
              {ESTADO_CONFIG[retiro.estado].label}
            </span>
            <button className="me-icon-btn" onClick={onClose}><CloseIcon size={18} /></button>
          </div>
        </div>

        {/* Items */}
        <div style={{ padding: 0, overflowX: 'auto' }}>
          <table className="me-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Producto</th>
                <th style={{ textAlign: 'right' }}>Solicitado</th>
                <th style={{ textAlign: 'right' }}>Entregado</th>
                <th style={{ textAlign: 'right' }}>Pendiente</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, i) => {
                const ventaItem = (item as unknown as { venta_items?: { productos?: { nombre: string; marca?: string } } }).venta_items
                const entregado  = entregas[item.id] ?? item.cantidad_entregada
                const pendiente  = item.cantidad_solicitada - entregado
                const completo   = pendiente <= 0

                return (
                  <tr key={item.id} style={{ background: i % 2 === 0 ? 'transparent' : 'var(--me-surface-2)' }}>
                    <td>
                      <div style={{ fontWeight: 500, fontSize: 13 }}>
                        {ventaItem?.productos?.nombre ?? '—'}
                      </div>
                      {ventaItem?.productos?.marca && (
                        <div style={{ fontSize: 11, color: 'var(--me-text-3)' }}>
                          {ventaItem.productos.marca}
                        </div>
                      )}
                    </td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--me-mono)', fontSize: 13 }}>
                      {item.cantidad_solicitada}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {retiro.estado === 'completo' ? (
                        <span style={{ fontFamily: 'var(--me-mono)', fontSize: 13, color: 'var(--me-ok)', fontWeight: 600 }}>
                          {entregado}
                        </span>
                      ) : (
                        <input
                          className="me-input"
                          style={{
                            fontFamily: 'var(--me-mono)', textAlign: 'center',
                            padding: '4px 6px', fontSize: 13, width: 64,
                            borderColor: completo ? 'var(--me-ok)' : undefined,
                          }}
                          type="number"
                          min={0}
                          max={item.cantidad_solicitada}
                          value={entregado}
                          onChange={e => setEntregas(prev => ({
                            ...prev,
                            [item.id]: Math.min(item.cantidad_solicitada, Math.max(0, parseInt(e.target.value) || 0)),
                          }))}
                        />
                      )}
                    </td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--me-mono)', fontSize: 13 }}>
                      {completo
                        ? <span style={{ color: 'var(--me-ok)' }}>✓</span>
                        : <span style={{ color: 'var(--me-warn)', fontWeight: 600 }}>{pendiente}</span>}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Notas */}
        {retiro.notas && (
          <div style={{ padding: '12px 24px 0' }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--me-text-3)', marginBottom: 4 }}>
              Notas
            </div>
            <div style={{ fontSize: 13, color: 'var(--me-text-2)' }}>{retiro.notas}</div>
          </div>
        )}

        {/* Footer */}
        <div style={{ display: 'flex', gap: 8, padding: '16px 24px', borderTop: '1px solid var(--me-line)', marginTop: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            className="me-btn me-btn-ghost"
            onClick={handleDescargarPDF}
            disabled={generandoPDF}
          >
            {generandoPDF ? 'Generando…' : 'Descargar PDF'}
          </button>
          {retiro.estado !== 'completo' && hayPendiente && (
            <button className="me-btn me-btn-ghost" onClick={entregarTodo}>
              Marcar todo entregado
            </button>
          )}
          <div style={{ flex: 1 }} />
          <button className="me-btn me-btn-ghost" onClick={onClose}>Cerrar</button>
          {retiro.estado !== 'completo' && (
            <button
              className="me-btn me-btn-primary"
              onClick={handleGuardar}
              disabled={registrar.isPending}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <CheckIcon size={14} strokeWidth={2} />
              {registrar.isPending ? 'Guardando…' : 'Registrar entrega'}
            </button>
          )}
        </div>
      </div>
    </>
  )
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function VentaCard({ venta, onDeselect }: { venta: Venta; onDeselect: () => void }) {
  const cliente = (venta as unknown as { clientes?: { nombre: string } }).clientes?.nombre
  return (
    <div style={{
      padding: '12px 14px', borderRadius: 10,
      background: 'var(--me-surface-2)', border: '1px solid var(--me-line)',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    }}>
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontFamily: 'var(--me-mono)' }}>#{String(venta.numero).padStart(5, '0')}</span>
          {venta.tipo === 'mayorista' && <span className="me-pill me-pill-mayor">Mayor</span>}
        </div>
        <div style={{ fontSize: 12, color: 'var(--me-text-3)', marginTop: 2 }}>
          {cliente ?? 'Sin cliente'} · {fmtFecha(venta.created_at)} · ${fmt(venta.total)}
        </div>
      </div>
      <button
        type="button"
        onClick={onDeselect}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--me-text-3)', padding: 4, borderRadius: 6,
          transition: 'color var(--me-dur-short)',
        }}
        onMouseEnter={e => (e.currentTarget.style.color = 'var(--me-text)')}
        onMouseLeave={e => (e.currentTarget.style.color = 'var(--me-text-3)')}
        title="Cambiar venta"
      >
        <CloseIcon size={14} strokeWidth={1.8} />
      </button>
    </div>
  )
}

function DrawerLabel({ children, optional }: { children: React.ReactNode; optional?: boolean }) {
  return (
    <div style={{
      fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase',
      color: 'var(--me-text-3)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6,
    }}>
      {children}
      {optional && (
        <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, fontSize: 11, color: 'var(--me-text-4)' }}>
          (opcional)
        </span>
      )}
    </div>
  )
}
